import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Plus, ArrowLeft } from "lucide-react";
import { Button } from "../../ui/button";

interface RouteInfo {
  title: string;
  description: string;
  actionPath?: string;
  actionLabel?: string;
  parentPath?: string;
  parentLabel?: string;
}

const routeMap: Record<string, RouteInfo> = {
  "/users": {
    title: "Users",
    description: "Manage and view all registered users, verification and driver status",
  },
  "/booked-rides": {
    title: "Booked Rides",
    description: "Monitor, track and manage all booked ride requests, passenger and trip details",
  },
  "/saved-rides": {
    title: "Saved Rides",
    description: "View and manage all rides saved by passengers across the platform",
  },
  "/vehicles": {
    title: "Vehicles",
    description: "Manage vehicle brands, models, types, colors and approval statuses"
  },
  "/transactions": {
    title: "Transactions",
    description: "Review all platform transactions, earnings and payment histories",
  },
  "/transfer": {
    title: "Transfer",
    description: "Manage driver transfer requests, payouts and settlement records",
  },
  "/coupons": {
    title: "Coupons",
    description: "Create, update and manage discount coupons and promotional codes"
  },
  "/created-rides": {
    title: "Created Rides",
    description: "View and track all published rides and schedules created by drivers",
  },
  "/earnings-and-tax": {
    title: "Earnings & Tax",
    description: "Track driver earnings, company commission and tax deductions",
  },
  "/reported-rides": {
    title: "Reported Rides",
    description: "Investigate and resolve passenger and driver ride complaints, issues and queries",
  },
  "/report-user": {
    title: "Reported Users",
    description: "Investigate and resolve user misconduct reports and complaints",
  },
  "/staff/add": {
    title: "Add Staff Member",
    description: "Create a new administrative staff member and assign module permissions",
    parentPath: "/staff/1/all",
    parentLabel: "Staff Management",
  },
  "/staff/edit": {
    title: "Edit Staff Member",
    description: "Update staff member details and access permissions",
    parentPath: "/staff/1/all",
    parentLabel: "Staff Management",
  },
  "/staff": {
    title: "Staff Management",
    description: "Manage admin staff members, roles and access permissions",
  },
  "/contents": {
    title: "Policies & Content",
    description: "Manage platform policies, legal terms, and informational content",
  },
  "/policies": {
    title: "Policy Details",
    description: "View and manage platform content, terms and legal agreements",
    parentPath: "/contents",
    parentLabel: "Policies & Content",
  },
  "/contact-us": {
    title: "Contact Us",
    description: "Review inquiries, messages and customer support requests",
  },
  "/FAQs": {
    title: "FAQs",
    description: "Manage frequently asked questions and help documentation",
    actionPath: "/faq/add",
    actionLabel: "Add FAQ",
  },
  "/settings": {
    title: "Settings",
    description: "Configure system settings, commission rates and global parameters",
  },
  "/profile/edit": {
    title: "Edit Profile",
    description: "Update your administrator details and profile picture",
    parentPath: "/profile",
    parentLabel: "Profile",
  },
  "/profile": {
    title: "Profile",
    description: "View and update your administrator account details and preferences",
  },
  "/change-password": {
    title: "Change Password",
    description: "Update your account credentials to keep your administrator access secure",
    parentPath: "/profile",
    parentLabel: "Profile",
  },
  "/notifications": {
    title: "Notifications",
    description: "View and send push notifications and announcements",
  },
};

const Breadcrumb = ({ pathname }: any) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Find matching route info
  const matchedKey = Object.keys(routeMap)
    .sort((a, b) => b.length - a.length)
    .find((key) => currentPath.startsWith(key));
  const info = matchedKey ? routeMap[matchedKey] : null;

  // Fallback title from path
  const pathParts = currentPath.split("/").filter(Boolean);
  const fallbackTitle = pathParts[0]
    ? pathParts[0].split("-").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ")
    : "Dashboard";

  // Dynamic policy subpage formatting (e.g. /policies/ABOUT_US)
  let subpageTitle = "";
  if (currentPath.startsWith("/policies/") && pathParts[1]) {
    subpageTitle = pathParts[1]
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  const title = subpageTitle || info?.title || fallbackTitle;
  const description =
    subpageTitle
      ? `View and manage platform content for ${subpageTitle}`
      : info?.description || `Overview and management for ${title.toLowerCase()}`;

  const parentPath = info?.parentPath;
  const parentLabel = info?.parentLabel;

  return (
    <div className="bg-white border-b border-slate-200/80 px-6 py-3.5 shadow-2xs">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          {/* Breadcrumbs navigation */}
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-slate-400 font-medium">
              <Link
                to="/"
                className="hover:text-[#006747] transition-colors text-slate-500"
              >
                Dashboard
              </Link>

              {parentPath && parentLabel && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  <Link
                    to={parentPath}
                    className="hover:text-[#006747] transition-colors text-slate-500"
                  >
                    {parentLabel}
                  </Link>
                </>
              )}

              <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
              <span className="text-[#006747] font-semibold">{title}</span>
            </nav>
          </div>

          {/* Title (H3) */}
          <h3 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight mt-1 mb-0">
            {title}
          </h3>

          {/* Description (Paragraph) */}
          <p className="text-xs sm:text-sm text-slate-500 mt-1 mb-0">
            {description}
          </p>
        </div>

        {/* Top-Right Action Buttons */}
        <div id="page-header-actions" className="flex items-center gap-2 self-start sm:self-center">
          {info?.actionPath && info?.actionLabel && currentPath !== info.actionPath && (
            <Link to={info.actionPath}>
              <Button
                variant="default"
                size="sm"
                className="gap-2 h-9 px-4 text-sm shadow-xs"
              >
                <Plus className="w-4 h-4" />
                <span>{info.actionLabel}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Breadcrumb;
